import { useEffect, useState } from "react";
import { ShieldCheck, Phone, Mail, User, Check, Loader2, CircleCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Palette echoing lawspecialties.com (Linda's firm site).
const NAVY = "#0d1430";
const GOLD = "#c4a052";
const CREAM = "#faf8f3";
const INK = "#1a1a2e";

const usd = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function CoveragePoint({ title, children }) {
  return (
    <div className="flex gap-3">
      <span
        className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full"
        style={{ backgroundColor: `${GOLD}22`, color: GOLD }}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
      <p className="text-[15px] leading-relaxed" style={{ color: "#3a3a46" }}>
        <span className="font-semibold" style={{ color: INK }}>{title}</span> {children}
      </p>
    </div>
  );
}

function EstimateCalculator() {
  const [hours, setHours] = useState(8);
  const [est, setEst] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/acsa-estimate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hours: Number(hours) || 0 }),
        });
        const data = await res.json();
        if (!cancelled) setEst(data);
      } catch {
        /* estimate is best-effort */
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [hours]);

  return (
    <div
      className="rounded-2xl border p-5 sm:p-6"
      style={{ borderColor: `${GOLD}40`, backgroundColor: "#fbf9f4" }}
      data-testid="welcome-calculator"
    >
      <h3 className="font-serif text-lg font-semibold" style={{ color: INK }}>Estimate your share</h3>
      <p className="mt-1 text-sm" style={{ color: "#5b5b66" }}>
        Move the slider to see roughly how the cost is shared at different amounts of work. These figures are estimates
        only — your actual matter may differ.
      </p>

      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <label htmlFor="welcome-hours" className="text-sm font-medium" style={{ color: INK }}>
            Hours of legal work
          </label>
          <span className="font-serif text-2xl font-semibold tabular-nums" style={{ color: INK }} data-testid="welcome-hours-value">
            {Number(hours).toFixed(1)} h
          </span>
        </div>
        <input
          id="welcome-hours"
          data-testid="welcome-hours-slider"
          type="range"
          min="0"
          max="40"
          step="0.5"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          className="mt-3 w-full"
          style={{ accentColor: GOLD }}
        />
        <div className="mt-1 flex justify-between text-[11px]" style={{ color: "#9a9aa2" }}>
          <span>0 h</span>
          <span>40 h</span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-xl bg-white p-3 ring-1" style={{ "--tw-ring-color": "#e7e2d6" }}>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: "#8a8a92" }}>Total fees</p>
          <p className="mt-1 font-serif text-lg font-semibold tabular-nums" style={{ color: INK }} data-testid="welcome-total-fees">
            {est ? usd(est.total_fees) : "—"}
          </p>
        </div>
        <div className="rounded-xl p-3 ring-1" style={{ backgroundColor: `${GOLD}1a`, "--tw-ring-color": `${GOLD}55` }}>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: "#8a6d22" }}>ACSA covers</p>
          <p className="mt-1 font-serif text-lg font-semibold tabular-nums" style={{ color: "#7a5f1c" }} data-testid="welcome-acsa-covers">
            {est ? usd(est.acsa_covers) : "—"}
          </p>
        </div>
        <div className="rounded-xl bg-white p-3 ring-1" style={{ "--tw-ring-color": "#e7e2d6" }}>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: "#8a8a92" }}>Your share</p>
          <p className="mt-1 font-serif text-lg font-semibold tabular-nums" style={{ color: INK }} data-testid="welcome-your-share">
            {est ? usd(est.your_share) : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, type = "text", icon: Icon, value, onChange, placeholder, testid, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium" style={{ color: INK }}>
        {label}
      </label>
      <div className="relative mt-1.5">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: "#a89b78" }} />
        <input
          id={id}
          data-testid={testid}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full rounded-xl border bg-white py-2.5 pl-10 pr-3 text-[15px] outline-none transition focus:ring-2"
          style={{ borderColor: "#d8d2c4", color: INK, "--tw-ring-color": `${GOLD}55` }}
        />
      </div>
    </div>
  );
}

function Crest({ logo }) {
  if (!logo) {
    return (
      <span
        className="grid h-16 w-16 place-items-center rounded-full border-2"
        style={{ borderColor: GOLD, color: GOLD }}
      >
        <ShieldCheck className="h-7 w-7" />
      </span>
    );
  }
  return <img src={logo} alt="Firm crest" className="h-20 w-auto object-contain" data-testid="welcome-crest" />;
}

export default function WelcomePage() {
  const [config, setConfig] = useState({ show_calculator: true, phone: "(858) 682-5400", firm_logo: "" });
  const [form, setForm] = useState({ name: "", personal_email: "", phone: "", notes: "", company: "" });
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/intake-config`)
      .then((r) => r.json())
      .then((d) => setConfig((c) => ({ ...c, ...d })))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!ack) {
      setError("Please confirm you've read and understand how ACSA coverage works.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, acknowledged: ack }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data?.detail;
        setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch {
      setError("We couldn't reach the office. Please check your connection and try again.");
      setSubmitting(false);
    }
  }

  const cardStyle = { borderColor: "#e7e2d6" };

  if (done) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: CREAM }}>
        <div className="mx-auto flex min-h-screen max-w-lg items-center px-5">
          <div
            className="w-full rounded-3xl border bg-white p-8 text-center shadow-sm"
            style={cardStyle}
            data-testid="welcome-confirmation"
          >
            <span
              className="mx-auto grid h-14 w-14 place-items-center rounded-full"
              style={{ backgroundColor: `${GOLD}22`, color: GOLD }}
            >
              <CircleCheck className="h-7 w-7" />
            </span>
            <h1 className="mt-5 font-serif text-2xl font-semibold" style={{ color: INK }}>Thank you</h1>
            <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "#4a4a55" }}>
              Linda's office has your information and will be in touch. If you have questions in the meantime, call us at{" "}
              <a href={`tel:${config.phone}`} className="font-semibold" style={{ color: "#9a7a24" }}>
                {config.phone}
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM }}>
      {/* Header band — echoes the firm site hero */}
      <div style={{ backgroundColor: NAVY }}>
        <div className="mx-auto flex max-w-2xl flex-col items-center px-5 py-8 text-center">
          <Crest logo={config.firm_logo} />
          <h2 className="mt-3 font-serif text-xl font-semibold tracking-wide sm:text-2xl" style={{ color: GOLD }}>
            The Law Office of Linda A. Albers
          </h2>
          <span className="mt-1 text-[11px] uppercase tracking-[0.25em]" style={{ color: "#b9bdcc" }}>
            Always Seeking Justice
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-5 pb-16">
        {/* Welcome */}
        <div className="-mt-4 rounded-3xl border bg-white p-6 shadow-sm sm:p-8" style={cardStyle} data-testid="welcome-page">
          <h1 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl" style={{ color: INK }}>Welcome</h1>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#4a4a55" }}>
            Facing a difficult situation at work is stressful, and questions about what it will cost only add to that.
            Here's some reassurance: as an ACSA member, you have a benefit that helps pay for your legal representation,
            and Linda's office will guide you through every step. This page explains how that coverage works and what
            you'll be responsible for — and lets us get a few details so we can reach you directly.
          </p>

          <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: `${GOLD}40`, backgroundColor: `${GOLD}12` }}>
            <h2 className="font-serif text-lg font-semibold" style={{ color: INK }}>A few details first</h2>
            <p className="mt-2 text-[15px] leading-relaxed" style={{ color: "#4a4a55" }}>
              So we can always reach you — your work email and phone may not be available to you right now — please share
              a personal email and phone number. This information is used only by Linda's office to stay in touch about
              your matter.
            </p>
          </div>
        </div>

        {/* How ACSA helps */}
        <div className="mt-5 rounded-3xl border bg-white p-6 shadow-sm sm:p-8" style={cardStyle}>
          <h2 className="font-serif text-2xl font-semibold tracking-tight" style={{ color: INK }}>
            How ACSA helps with your legal fees
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "#4a4a55" }}>
            As an ACSA member, your association helps pay for your representation. The benefit is real and generous — but
            it has limits, so it's worth understanding how it works:
          </p>

          <div className="mt-5 space-y-4">
            <CoveragePoint title="At the start, ACSA covers everything.">
              ACSA pays 100% of your first two hours of legal work.
            </CoveragePoint>
            <CoveragePoint title="After that, you and ACSA share the cost.">
              ACSA pays the larger share — 60% — and you pay the remaining 40%, up to ACSA's coverage limits.
            </CoveragePoint>
            <CoveragePoint title="Once ACSA reaches its limit, the rest is yours.">
              ACSA's coverage tops out at $5,600 in total. Beyond that, you're responsible for the full cost of further
              work.
            </CoveragePoint>
          </div>

          <p className="mt-5 text-[15px] leading-relaxed" style={{ color: "#4a4a55" }}>
            Work on ACSA matters is billed at $300 per hour. As an example, on a matter with about 8 hours of work
            (around $2,400), ACSA would cover roughly $1,680 and your share would be about $720. The longer or more
            complex your matter, the larger your share becomes.
          </p>

          {config.show_calculator && (
            <div className="mt-6">
              <EstimateCalculator />
            </div>
          )}
        </div>

        {/* What this means for you */}
        <div className="mt-5 rounded-3xl border bg-white p-6 shadow-sm sm:p-8" style={cardStyle}>
          <h2 className="font-serif text-2xl font-semibold tracking-tight" style={{ color: INK }}>What this means for you</h2>
          <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "#4a4a55" }}>
            ACSA's benefit will help a great deal, especially early on. But you should expect to be responsible for part
            of your legal fees — and for a longer matter, for the costs beyond ACSA's limit. You won't have to guess
            where things stand: Linda's office will keep you informed about your billing as your matter moves forward, so
            there are no surprises.
          </p>
          <p className="mt-4 text-sm leading-relaxed" style={{ color: "#7a7a84" }}>
            This page is a plain-language overview to help you understand your coverage. The formal terms of your
            representation and billing are in the engagement agreement you'll review and sign with Linda's office.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          className="mt-5 rounded-3xl border bg-white p-6 shadow-sm sm:p-8"
          style={cardStyle}
          data-testid="welcome-form"
        >
          {/* Honeypot — hidden from humans; bots fill it. */}
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={set("company")}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
            data-testid="welcome-company-honeypot"
          />

          <label
            className="flex cursor-pointer items-start gap-3 rounded-2xl border p-4"
            style={{ borderColor: `${GOLD}40`, backgroundColor: `${GOLD}10` }}
          >
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              data-testid="welcome-acknowledge"
              className="mt-0.5 h-5 w-5 shrink-0"
              style={{ accentColor: GOLD }}
            />
            <span className="text-[15px] leading-relaxed" style={{ color: "#3a3a46" }}>
              I've read and understand how ACSA coverage works, and that I'm financially responsible for the portion of
              fees ACSA does not cover.
            </span>
          </label>

          <div className="mt-5 space-y-4">
            <Field
              id="welcome-name"
              testid="welcome-name"
              label="Your name"
              icon={User}
              value={form.name}
              onChange={set("name")}
              placeholder="First and last name"
              autoComplete="name"
            />
            <Field
              id="welcome-email"
              testid="welcome-email"
              label="Personal email"
              type="email"
              icon={Mail}
              value={form.personal_email}
              onChange={set("personal_email")}
              placeholder="you@example.com"
              autoComplete="email"
            />
            <Field
              id="welcome-phone"
              testid="welcome-phone"
              label="Phone number"
              type="tel"
              icon={Phone}
              value={form.phone}
              onChange={set("phone")}
              placeholder="(555) 123-4567"
              autoComplete="tel"
            />
          </div>

          {error && (
            <p className="mt-4 rounded-xl px-4 py-3 text-sm" style={{ backgroundColor: "#fdecec", color: "#b3261e" }} data-testid="welcome-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="welcome-submit"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-[15px] font-semibold transition hover:brightness-110 disabled:opacity-60"
            style={{ backgroundColor: NAVY, color: GOLD, border: `1px solid ${GOLD}` }}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {submitting ? "Sending…" : "Send to Linda's office"}
          </button>
          <p className="mt-3 text-center text-xs" style={{ color: "#9a9aa2" }}>
            Your information is used only by Linda's office to stay in touch about your matter.
          </p>
        </form>
      </div>
    </div>
  );
}
